import { EditorView } from '@codemirror/view';

const MONOSPACE_FONT =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

// GitHub-style diff colors (work well on both light and dark backgrounds)
const DIFF_COLORS = {
  // Deleted (red)
  deletedLineBg: 'rgba(248, 81, 73, 0.15)',
  deletedTextBg: 'rgba(248, 81, 73, 0.4)',
  deletedGutter: 'rgba(248, 81, 73, 0.7)',
  deletedGutterBg: 'rgba(248, 81, 73, 0.1)',
  // Added (green)
  addedLineBg: 'rgba(63, 185, 80, 0.15)',
  addedTextBg: 'rgba(63, 185, 80, 0.4)',
  addedGutter: 'rgba(63, 185, 80, 0.7)',
  addedGutterBg: 'rgba(63, 185, 80, 0.1)',
} as const;

/**
 * Base editor styles shared by all merge views.
 */
const baseEditorStyles = {
  '&': {
    fontSize: '12px',
    backgroundColor: 'var(--colors-bg-canvas)',
    color: 'var(--colors-fg-default)',
    height: '100%',
  },
  '.cm-scroller': {
    overflow: 'auto',
    fontFamily: MONOSPACE_FONT,
    scrollbarWidth: 'thin' as const,
    scrollbarColor: 'var(--colors-border-outline) transparent',
  },
  '.cm-content': {
    fontFamily: MONOSPACE_FONT,
    padding: '4px 0',
  },
  '.cm-gutters': {
    border: 'none',
    backgroundColor: 'var(--colors-bg-subtle)',
    color: 'var(--colors-fg-muted)',
  },
  '.cm-line': {
    padding: '0 8px',
  },
  '&.cm-focused': {
    outline: 'none',
  },
  '.cm-changeGutter': {
    width: '4px',
  },
  '.cm-collapsedLines': {
    color: 'var(--colors-fg-muted)',
    cursor: 'pointer',
    fontSize: '11px',
    padding: '4px 8px',
    backgroundColor: 'var(--colors-bg-subtle)',
    borderTop: '1px solid var(--colors-border-subtle)',
    borderBottom: '1px solid var(--colors-border-subtle)',
  },
};

/**
 * Left side (original / deletions) — red highlights.
 */
export const mergeThemeLeft = EditorView.theme({
  ...baseEditorStyles,
  '.cm-changedLine': {
    backgroundColor: `${DIFF_COLORS.deletedLineBg} !important`,
  },
  '.cm-changedText': {
    backgroundColor: `${DIFF_COLORS.deletedTextBg} !important`,
    borderRadius: '2px',
  },
  '.cm-changeGutter .cm-gutterElement': {
    color: DIFF_COLORS.deletedGutter,
  },
});

/**
 * Right side (modified / additions) — green highlights.
 */
export const mergeThemeRight = EditorView.theme({
  ...baseEditorStyles,
  '.cm-changedLine': {
    backgroundColor: `${DIFF_COLORS.addedLineBg} !important`,
  },
  '.cm-changedText': {
    backgroundColor: `${DIFF_COLORS.addedTextBg} !important`,
    borderRadius: '2px',
  },
  '.cm-changeGutter .cm-gutterElement': {
    color: DIFF_COLORS.addedGutter,
  },
});

/**
 * Unified merge view — deletions are red, insertions are green.
 */
export const unifiedMergeTheme = EditorView.theme({
  ...baseEditorStyles,
  // Inserted (current doc) lines — green
  '.cm-changedLine': {
    backgroundColor: `${DIFF_COLORS.addedLineBg} !important`,
  },
  '.cm-changedText': {
    backgroundColor: `${DIFF_COLORS.addedTextBg} !important`,
    borderRadius: '2px',
  },
  // Deleted chunks (original doc shown above) — red
  '.cm-deletedChunk': {
    backgroundColor: DIFF_COLORS.deletedLineBg,
    borderLeft: `3px solid ${DIFF_COLORS.deletedGutter}`,
    fontStyle: 'normal',
  },
  '.cm-deletedChunk del': {
    textDecoration: 'none',
  },
  '.cm-deletedChunk del .cm-changedText': {
    backgroundColor: `${DIFF_COLORS.deletedTextBg} !important`,
  },
});

/**
 * Search panel theme — styles the Ctrl+F / Cmd+F search bar
 * to match the terra design system (aligned with HttpEditorSearchBar).
 */
export const searchPanelTheme = EditorView.theme({
  // Panel container
  '.cm-panels': {
    backgroundColor: 'var(--colors-bg-canvas)',
    color: 'var(--colors-fg-default)',
    borderTop: 'none',
    borderBottom: 'none',
  },
  '.cm-panels.cm-panels-top': {
    borderBottom: '1px solid var(--colors-border-default)',
  },
  '.cm-panels.cm-panels-bottom': {
    borderTop: '1px solid var(--colors-border-default)',
  },

  // Search form layout — allow wrapping for search + replace rows
  '.cm-search': {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 8px',
    fontSize: '12px',
    flexWrap: 'wrap',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  // Text inputs — override CM's default styles
  '.cm-textfield': {
    backgroundColor: 'var(--colors-bg-canvas) !important',
    color: 'var(--colors-fg-default) !important',
    border: '1px solid var(--colors-border-default) !important',
    borderRadius: '4px',
    padding: '3px 8px',
    height: '26px',
    fontSize: '12px',
    lineHeight: '20px',
    outline: 'none',
    fontFamily: MONOSPACE_FONT,
  },
  '.cm-textfield:focus': {
    borderColor: 'var(--colors-border-outline) !important',
    boxShadow: '0 0 0 1px var(--colors-border-outline)',
  },
  '.cm-textfield::placeholder': {
    color: 'var(--colors-fg-subtle)',
  },

  // Buttons — override CM's default linear-gradient background
  '.cm-button': {
    backgroundImage: 'none !important',
    backgroundColor: 'var(--colors-bg-muted) !important',
    color: 'var(--colors-fg-default) !important',
    border: '1px solid var(--colors-border-default) !important',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '3px 10px',
    height: '26px',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: '500',
    whiteSpace: 'nowrap' as const,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  '.cm-button:hover': {
    backgroundImage: 'none !important',
    backgroundColor: 'var(--colors-bg-muted) !important',
    color: 'var(--colors-fg-default) !important',
  },
  '.cm-button:active': {
    backgroundImage: 'none !important',
    backgroundColor: 'var(--colors-bg-muted) !important',
  },

  // Close button — make it look like an icon button
  '.cm-search button[name="close"]': {
    border: 'none !important',
    backgroundImage: 'none !important',
    backgroundColor: 'transparent !important',
    color: 'var(--colors-fg-muted) !important',
    fontSize: '16px',
    fontWeight: '400',
    padding: '0',
    width: '26px',
    height: '26px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    flexShrink: '0',
  },
  '.cm-search button[name="close"]:hover': {
    backgroundImage: 'none !important',
    backgroundColor: 'var(--colors-bg-muted) !important',
    color: 'var(--colors-fg-default) !important',
  },

  // Checkbox labels (case, re, word)
  '.cm-search label': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '12px',
    color: 'var(--colors-fg-default)',
    cursor: 'pointer',
    padding: '2px 4px',
    borderRadius: '4px',
    height: '26px',
    whiteSpace: 'nowrap' as const,
    userSelect: 'none' as const,
  },
  '.cm-search label:hover': {
    color: 'var(--colors-fg-default)',
    backgroundColor: 'var(--colors-bg-muted)',
  },

  // Checkboxes
  '.cm-search input[type="checkbox"]': {
    accentColor: 'var(--colors-fg-default)',
    width: '13px',
    height: '13px',
    cursor: 'pointer',
  },

  // Search match highlighting in the editor
  '.cm-searchMatch': {
    backgroundColor: 'rgba(255, 223, 93, 0.4)',
    borderRadius: '2px',
  },
  '.cm-searchMatch-selected': {
    backgroundColor: 'rgba(255, 223, 93, 0.7)',
    outline: '2px solid rgba(210, 169, 34, 0.8)',
    outlineOffset: '-1px',
  },
});
